/**
 * {deliveryNote}の一覧 /delivery-notes（#266、docs/96 v0.2）。左のメニューの受注の組（見積書と請求書の間。使う機能「{deliveryNote}」の会社だけ）。
 *   タブ：下書き／発行済（未請求）／請求済／すべて（件数つき）。探す（{customer}・番号・件名）。
 *   列：納品日・No.・{customer}・{project}・件名・金額（税抜）・状態・請求書
 *   発行済のタブでは行を選んで「選んだ{deliveryNote}で請求書作成」（束ね請求。同じ受注（見積なしは同じ{project}）だけ）。
 *   「＋ {deliveryNote}作成」＝{customer}を選ぶだけ（{project}なし）。見積からは受注した見積の画面、{project}からは{project}のタブ
 *   {project}の画面のタブ「{deliveryNote}」・見積の画面にも同じ表（GroupDeliveryNotes）
 *   下書きの行の右端に「⋯」→「下書き削除」（窓は画面と同じ components/DeleteDraftDialog。docs/96 §13.5）
 *   1b：タブ「{acceptance}」（使う機能「{acceptance}」の会社だけ）＝検収書の一覧（状態 下書き／未確認／検収済）・行は /acceptances/:id・「＋ {acceptance}作成」
 */
import { ACCEPTANCE_MSG, DELIVERY_NOTE_MSG, DELIVERY_NOTE_TAB_LABEL, deliveryNoteStateText, unissuedTitle, type DeliveryNoteList, type DeliveryNoteSummary, type DeliveryNoteTab } from "@genbaryoku/contract";
import { useMe } from "../../auth.js";
import { uses } from "../../core/uses.js";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { usePageName } from "../../components/BackButton.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { DeleteDraftDialog } from "../../components/DeleteDraftDialog.jsx";
import { DOTS_H } from "../../components/HeadActions.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { RowMenu } from "../../components/RowMenu.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ErrorText, useToast } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { mdSlash, yen } from "../../lib/num.js";
import { BundleInvoiceDialog } from "./BundleInvoiceDialog.jsx";

const TABS: DeliveryNoteTab[] = ["draft", "issued", "billed", "all"];
/** タブ（1b：使う機能「{acceptance}」の会社だけ「{acceptance}」を足す） */
function useTabs(): DeliveryNoteTab[] {
  const me = useMe().data;
  return uses(me, "acceptance") && !!me ? [...TABS, "acceptance"] : TABS;
}

export function DeliveryNoteListPage() {
  const [sp, setSp] = useSearchParams();
  const tabs = useTabs();
  const tab = (tabs.find((k) => k === sp.get("tab")) ?? "all") as DeliveryNoteTab;
  usePageName(t(DELIVERY_NOTE_MSG.list));
  return (
    <ListHead title={t(DELIVERY_NOTE_MSG.list)}>
      <NoteTable
        create={
          tab === "acceptance" ? (
            <Link className="btn primary" to="/delivery-notes/new?kind=acceptance" data-testid="ac-new">
              <Icon k="plus" />
              {t(ACCEPTANCE_MSG.create)}
            </Link>
          ) : (
            <Link className="btn primary" to="/delivery-notes/new" data-testid="dn-new">
              <Icon k="plus" />
              {t(DELIVERY_NOTE_MSG.create)}
            </Link>
          )
        }
        tab={tab}
        onTab={(k) =>
          setSp((p) => {
            const n = new URLSearchParams(p);
            if (k === "all") n.delete("tab");
            else n.set("tab", k);
            return n;
          })
        }
      />
    </ListHead>
  );
}

/** {project}の画面のタブ「{deliveryNote}」（その{project}の{deliveryNote}と「{deliveryNote}作成」＝見積なし） */
export function GroupDeliveryNotes({ groupId, side }: { groupId: string; side?: ReactNode }) {
  const [tab, setTab] = useState<DeliveryNoteTab>("all");
  return (
    <div data-testid="group-delivery-notes" style={{ marginTop: 12 }}>
      <NoteTable tab={tab} onTab={setTab} group={groupId} side={side} />
    </div>
  );
}

/** 一覧（create を渡すと一覧の頭の形＝表のカード／{project}の画面＝side を件数の行の右に） */
function NoteTable({ tab, onTab, group, side, create }: { tab: DeliveryNoteTab; onTab: (k: DeliveryNoteTab) => void; group?: string; side?: ReactNode; create?: ReactNode }) {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [bundle, setBundle] = useState(false);
  const [del, setDel] = useState<string | null>(null);
  const { toast, node: toastNode } = useToast();
  useEffect(() => {
    const h = window.setTimeout(() => setQApplied(q.trim()), 300);
    return () => window.clearTimeout(h);
  }, [q]);
  const list = useQuery({
    queryKey: ["delivery-notes", tab, qApplied, group ?? ""],
    queryFn: () => api<DeliveryNoteList>("GET", `/delivery-notes?tab=${tab}${qApplied ? `&q=${encodeURIComponent(qApplied)}` : ""}${group ? `&group=${group}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const items: DeliveryNoteSummary[] = list.data?.items ?? [];
  const tabs = useTabs();
  const open = (x: DeliveryNoteSummary) => nav(x.kind === "acceptance" ? `/acceptances/${x.id}` : `/delivery-notes/${x.id}`);
  const tid = group ? "gdn" : "dnl";
  const search = <FilterInput value={q} onChange={setQ} placeholder={t(DELIVERY_NOTE_MSG.searchLabel)} testId="dn-search" />;
  const card = create !== undefined;
  // 束ね請求：発行済（未請求）の行だけ選べる。違う受注・{project}が混じれば押せない
  const selectable = (x: DeliveryNoteSummary) => x.kind === "deliveryNote" && (x.state === "issued" || x.state === "received");
  const pickedRows = items.filter((x) => picked.includes(x.id));
  const mixed = new Set(pickedRows.map((x) => x.billKey)).size > 1 || new Set(pickedRows.map((x) => x.clientId)).size > 1;
  const first = pickedRows[0];
  const sum = (
    <div className="pul-sum" data-testid="dn-sum">
      <span>
        <b className="num">{list.data ? items.length : "…"}</b> 件
      </span>
      <span>
        合計（税抜） <b className="num">{list.data ? yen(list.data.subtotal) : "…"}</b> 円
      </span>
      <span className="sp" />
      {pickedRows.length ? (
        <>
          {mixed ? <span className="shu" data-testid="dn-bundle-mixed">{t(DELIVERY_NOTE_MSG.bundleMixed)}</span> : null}
          <button type="button" className="btn primary" disabled={mixed} onClick={() => setBundle(true)} data-testid="dn-bundle-open">
            {t(DELIVERY_NOTE_MSG.selectToBill)}
          </button>
        </>
      ) : null}
      {side}
    </div>
  );
  const showPick = items.some(selectable);
  const showMore = items.some((x) => x.state === "draft");
  const cols = 7 + (group ? 0 : 1) + (showPick ? 1 : 0) + (showMore ? 1 : 0);
  return (
    <>
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <div className={card ? "lcard" : undefined}>
        <ListTabs
          id={tid}
          label={t(DELIVERY_NOTE_MSG.tabsLabel)}
          tabs={tabs.map((k) => ({ key: k, label: t(DELIVERY_NOTE_TAB_LABEL[k]), count: list.data?.counts[k] ?? "…" }))}
          value={tab}
          onChange={(k) => {
            setPicked([]);
            onTab(k as DeliveryNoteTab);
          }}
          filter={search}
          create={create}
        />
        <div className="tbl-wrap ltab-panel" {...listTabPanel(tid, tab)}>
          {card ? null : sum}
          <table className="list dnl-list" data-testid="dn-list">
            <thead>
              <tr>
                {showPick ? <th className="pick" aria-label="選択" /> : null}
                <th>{DELIVERY_NOTE_MSG.colIssueDate}</th>
                <th>{DELIVERY_NOTE_MSG.colNo}</th>
                <th>{L.customer}</th>
                {group ? null : <th>{L.project}</th>}
                <th>{DELIVERY_NOTE_MSG.colSubject}</th>
                <th className="amt">{DELIVERY_NOTE_MSG.colAmount}</th>
                <th>{DELIVERY_NOTE_MSG.colState}</th>
                <th>{DELIVERY_NOTE_MSG.colInvoice}</th>
                {showMore ? <th className="more" aria-label="操作" /> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((x) => (
                <tr key={x.id} className="r" tabIndex={0} onClick={() => open(x)} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && open(x)} data-testid="dn-row">
                  {showPick ? (
                    <td className="pick" onClick={(e) => e.stopPropagation()}>
                      {selectable(x) ? (
                        <input type="checkbox" aria-label={`${x.documentNo ?? ""} を選ぶ`} checked={picked.includes(x.id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, x.id] : p.filter((y) => y !== x.id)))} data-testid={`dn-pick-${x.documentNo}`} />
                      ) : null}
                    </td>
                  ) : null}
                  <td className="num">{mdSlash(x.issueDate)}</td>
                  <td className="num">{x.documentNo ?? <span className="muted">—</span>}</td>
                  <td>
                    <b>{x.clientName}</b>
                  </td>
                  {group ? null : <td>{x.group?.name ?? <span className="muted">—</span>}</td>}
                  <td>{x.subject || <span className="muted">—</span>}</td>
                  <td className="num amt">{yen(x.subtotal)}</td>
                  <td className="statecell">
                    <span className={"chip " + (x.state === "draft" || x.state === "unissued" || (x.kind === "acceptance" && x.state === "issued") ? "quiet" : "ai")} data-testid="dn-state" title={x.state === "unissued" && x.documentNo ? unissuedTitle(x.documentNo) : undefined}>{deliveryNoteStateText(x.state, x.kind)}</span>
                  </td>
                  <td className="num">
                    {x.invoice ? (
                      <Link to={`/invoices/${x.invoice.id}`} onClick={(e) => e.stopPropagation()}>{x.invoice.documentNo ?? "下書き"}</Link>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  {showMore ? (
                    <td className="more" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                      {x.state === "draft" ? (
                        <RowMenu
                          label={t(DELIVERY_NOTE_MSG.rowMenu).replace("{name}", x.subject || x.clientName)}
                          items={[{ label: DELIVERY_NOTE_MSG.deleteDraft, danger: true, onSelect: () => setDel(x.id), testId: "dn-row-delete" }]}
                          testId="dn-row-more"
                          trigger={DOTS_H}
                          triggerClass="rowmore dots"
                        />
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
              {list.data && !items.length ? (
                <tr className="emptyrows">
                  <td colSpan={cols}>{t(DELIVERY_NOTE_MSG.empty)}</td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {card ? sum : null}
        </div>
      </div>
      {bundle && first ? (
        <BundleInvoiceDialog
          seed={{ id: first.id, sourceQuote: null, projectGroup: first.group ? { id: first.group.id } : null }}
          preselect={picked}
          onClose={() => setBundle(false)}
        />
      ) : null}
      {del ? (
        <DeleteDraftDialog
          prefix="dn"
          onClose={() => setDel(null)}
          onConfirm={async () => {
            await api<void>("DELETE", `/delivery-notes/${del}`);
            void qc.invalidateQueries({ queryKey: ["delivery-notes"] });
            void qc.invalidateQueries({ queryKey: ["delivery-state"] });
            void qc.invalidateQueries({ queryKey: ["home"] });
            setDel(null);
            toast(t(DELIVERY_NOTE_MSG.deleted), undefined, "success", "dn-deleted-notice");
          }}
        />
      ) : null}
      {toastNode}
    </>
  );
}
